import React, { useState } from "react";
import {
  Layout,
  Card,
  Input,
  Button,
  Typography,
  notification,
  Space,
  Row,
  Col,
} from "antd";
import {
  TeamOutlined,
  PlusCircleOutlined,
  KeyOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useMutation } from "react-query";
import {
  createCompanyApi,
  joinCompanyByCodeApi,
} from "../../../service/authApi";
import Cookies from "js-cookie";

const { Title, Text, Paragraph } = Typography;

interface OnboardingViewProps {
  refetch: () => void;
}

export const OnboardingView: React.FC<OnboardingViewProps> = ({ refetch }) => {
  const [companyName, setCompanyName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const accessToken = Cookies.get("accessToken") || "";

  const { mutateAsync: doCreateCompany, isLoading: isCreating } = useMutation(
    (name: string) => createCompanyApi(name, accessToken),
    {
      onSuccess: (data) => {
        notification.success({
          message: "Firma utworzona!",
          description: (
            <div>
              <p>Firma <strong>{data.companyName}</strong> została utworzona.</p>
              <p>
                Kod zaproszenia:{" "}
                <strong style={{ fontSize: 18, letterSpacing: 2 }}>
                  {data.joinCode}
                </strong>
              </p>
              <p style={{ fontSize: 12, color: "#888" }}>
                Udostępnij ten kod swoim pracownikom, aby mogli dołączyć do firmy.
              </p>
            </div>
          ),
          duration: 10,
          style: { background: "#52c41a30" },
        });
        refetch();
      },
      onError: (error: any) => {
        notification.error({
          message: "Błąd tworzenia firmy",
          description: error?.message || "Spróbuj ponownie",
          style: { background: "#ff4d4f30" },
        });
      },
    }
  );

  const { mutateAsync: doJoinCompany, isLoading: isJoining } = useMutation(
    (code: string) => joinCompanyByCodeApi(code, accessToken),
    {
      onSuccess: (data) => {
        notification.success({
          message: "Dołączono do firmy!",
          description: `Zostałeś dodany do firmy "${data.companyName}".`,
          style: { background: "#52c41a30" },
        });
        refetch();
      },
      onError: (error: any) => {
        notification.error({
          message: "Błąd dołączania",
          description: error?.message || "Sprawdź kod i spróbuj ponownie",
          style: { background: "#ff4d4f30" },
        });
      },
    }
  );

  const handleCreateCompany = () => {
    if (!companyName.trim()) {
      notification.warning({ message: "Podaj nazwę firmy" });
      return;
    }
    doCreateCompany(companyName.trim());
  };

  const handleJoinCompany = () => {
    if (!joinCode.trim()) {
      notification.warning({ message: "Podaj kod zaproszenia" });
      return;
    }
    doJoinCompany(joinCode.trim().toUpperCase());
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 800, width: "100%" }}>
        <Space
          direction="vertical"
          size="large"
          style={{ width: "100%", textAlign: "center", marginBottom: 32 }}
        >
          <Title level={2} style={{ color: "#fff", margin: 0 }}>
            Witaj w Flex Management!
          </Title>
          <Text style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: 16 }}>
            Aby rozpocząć, utwórz nową firmę lub dołącz do istniejącej za pomocą kodu zaproszenia.
          </Text>
        </Space>

        <Row gutter={[24, 24]}>
          {/* Create Company Card */}
          <Col xs={24} md={12}>
            <Card
              hoverable
              style={{
                borderRadius: 16,
                height: "100%",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "none",
              }}
              bodyStyle={{ padding: 32 }}
            >
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <PlusCircleOutlined
                    style={{ fontSize: 28, color: "#fff" }}
                  />
                </div>

                <div style={{ textAlign: "center" }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Stwórz firmę
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={{ marginTop: 8, marginBottom: 0 }}
                  >
                    Utwórz nową firmę i zaproś swoich pracowników za pomocą
                    kodu zaproszenia.
                  </Paragraph>
                </div>

                <Input
                  size="large"
                  placeholder="Nazwa firmy"
                  prefix={<TeamOutlined />}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onPressEnter={handleCreateCompany}
                  style={{ borderRadius: 8 }}
                />

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<ArrowRightOutlined />}
                  loading={isCreating}
                  onClick={handleCreateCompany}
                  style={{
                    height: 44,
                    borderRadius: 8,
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                  }}
                >
                  Utwórz firmę
                </Button>
              </Space>
            </Card>
          </Col>

          {/* Join Company Card */}
          <Col xs={24} md={12}>
            <Card
              hoverable
              style={{
                borderRadius: 16,
                height: "100%",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "none",
              }}
              bodyStyle={{ padding: 32 }}
            >
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  <KeyOutlined style={{ fontSize: 28, color: "#fff" }} />
                </div>

                <div style={{ textAlign: "center" }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Dołącz do firmy kodem
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={{ marginTop: 8, marginBottom: 0 }}
                  >
                    Wpisz 6-znakowy kod zaproszenia otrzymany od administratora
                    firmy.
                  </Paragraph>
                </div>

                <Input
                  size="large"
                  placeholder="Np. A3X7K9"
                  prefix={<KeyOutlined />}
                  value={joinCode}
                  onChange={(e) =>
                    setJoinCode(e.target.value.toUpperCase().slice(0, 6))
                  }
                  onPressEnter={handleJoinCompany}
                  maxLength={6}
                  style={{
                    borderRadius: 8,
                    textAlign: "center",
                    letterSpacing: 4,
                    fontSize: 18,
                    fontWeight: 600,
                  }}
                />

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<ArrowRightOutlined />}
                  loading={isJoining}
                  onClick={handleJoinCompany}
                  style={{
                    height: 44,
                    borderRadius: 8,
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    border: "none",
                  }}
                >
                  Dołącz do firmy
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button
            type="link"
            onClick={() => {
              Cookies.remove("accessToken");
              Cookies.remove("refreshToken");
              refetch();
            }}
            style={{ color: "rgba(255, 255, 255, 0.7)" }}
          >
            Wyloguj się
          </Button>
        </div>
      </div>
    </Layout>
  );
};
